import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { isValidEmail } from '../../../../utils/contact';
import { extractFieldErrors } from '../../../../services/practiceDetail';
import type {
    OrthodontistDetailInfo,
    UpdateOrthodontistBody
} from '../../../../services/orthodontistAccounts';
import { useUpdateOrthodontistDetail } from '../../../../hooks/useOrthodontistDetail';
import { maxLengthFieldError } from '../../practice/details/fieldDefinitions';
import type { FormPhase } from '../../practice/details/usePracticeDetailsForm';
import { requiredOrthodontistFieldError } from '../create/orthodontistCreateFields';
import {
    buildOrthodontistInfoUpdate,
    flattenOrthodontistInfo,
    ORTHODONTIST_FIELD_DEFS
} from './orthodontistInfoFields';

function changedFieldError(key: string, value: string): string | null {
    const field = ORTHODONTIST_FIELD_DEFS[key];
    if (field.type === 'email' && value !== '' && !isValidEmail(value)) {
        return "isn't a valid email address";
    }
    return requiredOrthodontistFieldError(field, value) ?? maxLengthFieldError(field, value);
}

function validateChanges(changes: UpdateOrthodontistBody): Record<string, string> {
    return Object.fromEntries(
        Object.entries(changes).flatMap(([key, value]) => {
            const message = changedFieldError(key, value ?? '');
            return message ? [[key, message]] : [];
        })
    );
}

function withoutKey(record: Record<string, string>, key: string): Record<string, string> {
    if (!(key in record)) return record;
    return Object.fromEntries(Object.entries(record).filter(([k]) => k !== key));
}

function formPhase(isSaving: boolean, dirtyCount: number, errorCount: number): FormPhase {
    if (isSaving) return 'saving';
    if (dirtyCount === 0) return 'idle';
    return errorCount > 0 ? 'error' : 'dirty';
}

export function useOrthodontistInfoForm(orthodontist: OrthodontistDetailInfo) {
    const { showToast } = useToast();
    const { mutateAsync, isPending } = useUpdateOrthodontistDetail(orthodontist.id);
    const [edits, setEdits] = useState<Record<string, string>>({});
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const recordStamp = `${orthodontist.id}:${orthodontist.updatedAt}`;
    useEffect(() => {
        setEdits({});
        setFieldErrors({});
    }, [recordStamp]);

    const values = useMemo(
        () => ({ ...flattenOrthodontistInfo(orthodontist), ...edits }),
        [orthodontist, edits]
    );
    const changes = useMemo(
        () => buildOrthodontistInfoUpdate(orthodontist, values),
        [orthodontist, values]
    );
    const dirtyKeys = Object.keys(changes);
    const phase = formPhase(isPending, dirtyKeys.length, Object.keys(fieldErrors).length);

    const onFieldChange = useCallback((key: string, value: string) => {
        setEdits((prev) => ({ ...prev, [key]: value }));
        setFieldErrors((prev) => withoutKey(prev, key));
    }, []);

    const onSave = useCallback(async () => {
        const localErrors = validateChanges(changes);
        if (Object.keys(localErrors).length > 0) {
            setFieldErrors(localErrors);
            return;
        }
        try {
            await mutateAsync(changes);
            setEdits({});
            setFieldErrors({});
            showToast('Orthodontist details saved', 'success');
        } catch (err) {
            setFieldErrors(extractFieldErrors(err) ?? {});
            showToast(apiErrorMessage(err, 'Failed to save orthodontist details'), 'error');
        }
    }, [changes, mutateAsync, showToast]);

    const onDiscard = useCallback(() => {
        setEdits({});
        setFieldErrors({});
    }, []);

    return {
        values,
        fieldError: (key: string) => fieldErrors[key],
        onFieldChange,
        phase,
        dirtyCount: dirtyKeys.length,
        dirtyLabels: dirtyKeys.map((key) => ORTHODONTIST_FIELD_DEFS[key].label),
        isSaving: isPending,
        onSave: () => void onSave(),
        onDiscard
    };
}
